/**
 * PATH: packages/ui/src/kanban/components/lane/useVirtualizedLaneCards.ts
 * FILE_ID: UI-KANBAN-HOOK-308
 * PURPOSE: Windowed virtualization for a lane's card list (arc V7):
 *          @tanstack/react-virtual with measureElement for variable card
 *          heights, gated to Split-view lanes over a card-count threshold
 *          and behind a localStorage feature flag.
 * WHY: The Split view stacks many lanes per band, so one heavy band can
 *      mount thousands of card nodes. content-visibility (UAT P4-b) skips
 *      offscreen PAINT but the DOM nodes still exist; windowing drops the
 *      node count itself. Flag-staged while the V1 KPIs
 *      (time_to_first_split_paint + BoardSwimlaneSplitView render profile)
 *      size the win in production.
 * FLOW:
 * ┌───────────────────────┐   ┌──────────────────┐   ┌─────────────────────┐
 * │ requested + flag +    │ → │ useVirtualizer   │ → │ {active, items,     │
 * │ count >= threshold    │   │ (window+measure) │   │  totalSize, measure}│
 * └───────────────────────┘   └──────────────────┘   └─────────────────────┘
 * DEPENDENCIES:
 * - @tanstack/react-virtual: the windowing engine (root dep ^3.10.8)
 * - laneVirtualizeRegistry (UI-KANBAN-LANE-VREG-001): scroll-to-card bridge
 * RELATED:
 * - LaneBody.tsx: renders the virtual window when `active`
 * - useSplitPerfKpis (UI-KANBAN-HOOK-306): measures the effect of this arc
 */
import { useEffect, useMemo } from 'react'
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual'

import {
  LANE_VIRTUALIZE_FLAG_KEY,
  LANE_VIRTUALIZE_FLAT_FLAG_KEY,
  LANE_VIRTUALIZE_THRESHOLD,
} from './constants'
import {
  registerVirtualizedLane,
  unregisterVirtualizedLane,
} from './laneVirtualizeRegistry'
import { shouldVirtualizeCardSurface } from '../../card-surface/performance/virtualizationPolicy'

/** The lane surface requesting virtualization. `true` (Split view) and
 *  `'flat'` (flat board, arc V8) are gated on SEPARATE localStorage flags
 *  so the two rollouts stage independently. */
export type LaneVirtualizeRequest = boolean | 'flat'

/** ON by default since 2026-07-09 (the staged-rollout flag graduated —
 *  see lane/constants.ts). The per-surface localStorage keys are now
 *  OPT-OUT kill switches: set `kanban:lane-virtualize` (Split, arc V7)
 *  or `kanban:lane-virtualize-flat` (flat board, arc V8) to '0' to force
 *  the flow render on that surface. SSR/jsdom-safe (no window → default
 *  on; the threshold gate still applies). */
export function isLaneVirtualizeEnabled(
  surface: 'split' | 'flat' = 'split',
): boolean {
  const key =
    surface === 'flat' ? LANE_VIRTUALIZE_FLAT_FLAG_KEY : LANE_VIRTUALIZE_FLAG_KEY
  try {
    return (
      typeof window === 'undefined' ||
      window.localStorage.getItem(key) !== '0'
    )
  } catch {
    return true
  }
}

/** f(requested, flagOn, count) -> whether the lane should window its cards.
 *  Pure so the gate is unit-testable without mounting a virtualizer. */
export function shouldVirtualizeLane(
  requested: boolean,
  flagOn: boolean,
  count: number,
): boolean {
  return shouldVirtualizeCardSurface({
    surface: 'split',
    requested,
    enabled: flagOn,
    itemCount: count,
    threshold: LANE_VIRTUALIZE_THRESHOLD,
  })
}

/** Estimated row height (card face + the 6px stack gap) per density, kept in
 *  lockstep with the containIntrinsicSize values in LaneCardItem so the
 *  scrollbar doesn't jump while measureElement corrects real heights. */
export function estimateLaneCardSize(
  displayMode: string | undefined,
  cardDensity: 'compact' | 'expanded' | 'large' | undefined,
): number {
  if (displayMode === 'checklist') return 42
  if (cardDensity === 'compact') return 50
  if (cardDensity === 'large') return 130
  return 102
}

export interface UseVirtualizedLaneCardsArgs {
  /** Which surface (if any) opted this lane in: `true` = Split view
   *  (arc V7), `'flat'` = flat board (arc V8), false/undefined = none. */
  requested: LaneVirtualizeRequest
  laneId: string
  itemCount: number
  /** Flattened render-order card ids (for the scroll-to-card bridge). */
  cardIds: readonly string[]
  scrollRef: React.RefObject<HTMLDivElement | null>
  displayMode: string | undefined
  cardDensity: 'compact' | 'expanded' | 'large' | undefined
}

export interface VirtualizedLaneCards {
  active: boolean
  items: VirtualItem[]
  totalSize: number
  measureElement: (el: Element | null) => void
}

export function useVirtualizedLaneCards({
  requested,
  laneId,
  itemCount,
  cardIds,
  scrollRef,
  displayMode,
  cardDensity,
}: UseVirtualizedLaneCardsArgs): VirtualizedLaneCards {
  const surface = requested === 'flat' ? 'flat' : 'split'
  const active = shouldVirtualizeCardSurface({
    surface,
    requested: !!requested,
    enabled: isLaneVirtualizeEnabled(surface),
    itemCount,
    threshold: LANE_VIRTUALIZE_THRESHOLD,
  })
  const estimate = estimateLaneCardSize(displayMode, cardDensity)

  // Hooks can't be conditional — the virtualizer always mounts but with
  // count 0 when inactive, which costs one no-op object per lane.
  const virtualizer = useVirtualizer({
    count: active ? itemCount : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimate,
    overscan: 8,
  })

  // Scroll-to-card bridge: focusCardInView / board find query the DOM by
  // data-card-id, which misses unmounted (off-window) cards. Register this
  // lane so those helpers can scrollToIndex first (see registry FILE_ID).
  const idIndex = useMemo(() => {
    if (!active) return null
    const m = new Map<string, number>()
    cardIds.forEach((id, i) => m.set(id, i))
    return m
  }, [active, cardIds])

  useEffect(() => {
    if (!active || !idIndex) return
    registerVirtualizedLane(laneId, {
      indexOfCard: (cardId) => idIndex.get(cardId) ?? -1,
      scrollToIndex: (index) =>
        virtualizer.scrollToIndex(index, { align: 'center' }),
    })
    return () => unregisterVirtualizedLane(laneId)
  }, [active, idIndex, laneId, virtualizer])

  return {
    active,
    items: active ? virtualizer.getVirtualItems() : [],
    totalSize: active ? virtualizer.getTotalSize() : 0,
    measureElement: virtualizer.measureElement,
  }
}
